import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus } from 'react-icons/fa';
import { 
  changeTopic, 
  addTopic, 
  removeTopicFromBasket, 
  setBasketContentChanged, 
  setCurrentBasket,
  fetchBasketById,
  selectCurrentBasket,
  selectBasketTopics,
  updateBasket,
  updateTopic,
} from '../../store/workspacesSlice';
import TopicSelectionModal from '../TopicSelectionModal/TopicSelectionModal';
import TopicItem from './TopicItem';
import BasketHeader from './BasketHeader';
import EditBasketModal from '../WorkspaceDetail/EditBasketModal';
import { toast } from 'react-toastify';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import RelationDescriptionModal from './RelationDescriptionModal';
import BasketStats from './BasketStats';
import MasteryCelebration from './MasteryCelebration';
import { TipBox } from '../TipBox';

const BasketContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: basketId } = useParams();
  const location = useLocation();
  const { basket: initialBasket, workspaceId, workspaceName, searchTerm } = location.state || {};

  const effectiveBasketId = basketId || initialBasket?.id;
  
  const [hasChanges, setHasChanges] = useState(false);
  const currentBasket = useSelector(selectCurrentBasket);

  // Memoize these values to prevent unnecessary re-renders
  const memoizedWorkspaceId = useMemo(() => workspaceId, [workspaceId]);
  const memoizedWorkspaceName = useMemo(() => workspaceName, [workspaceName]);

  useEffect(() => {
    if (effectiveBasketId && !currentBasket) {
      dispatch(fetchBasketById(effectiveBasketId))
        .then(() => {
          dispatch(setCurrentBasket(effectiveBasketId));
        })
        .catch((error) => {
          console.error('Failed to fetch basket:', error);
        });
    }
  }, [dispatch, effectiveBasketId, currentBasket]);

  const basketTopics = useSelector((state) => selectBasketTopics(state, effectiveBasketId));

  // State declarations
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicSelectionModalOpen, setTopicSelectionModalOpen] = useState(false);
  const [editBasketModalOpen, setEditBasketModalOpen] = useState(false);
  const [showRelationModal, setShowRelationModal] = useState(false);
  const [showMasteryCelebration, setShowMasteryCelebration] = useState(false);
  const [previousMasteryPercentage, setPreviousMasteryPercentage] = useState(null);
  const [celebrationTimeout, setCelebrationTimeout] = useState(null);

  // Handler for removing a topic from the basket
  const handleRemoveTopicFromBasket = useCallback(async (topicId) => {
    try {
      await dispatch(removeTopicFromBasket({ topicId, basketId: effectiveBasketId, workspaceId: memoizedWorkspaceId })).unwrap();
    } catch (error) {
      console.error('Failed to remove topic from basket:', error);
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId]);

  // Handler for editing a topic
  const handleEditTopic = useCallback((topic) => {
    if (topic.showRelationTab) {
      setSelectedTopic(topic);
      setShowRelationModal(true);
    }
  }, []);

  // Handle adding a completely new topic
  const handleAddNewTopic = useCallback(async (newTopicData) => {
    try {
      const topicPayload = newTopicData.workspaceId ? 
        newTopicData : 
        {
          workspaceId: memoizedWorkspaceId,
          newTopic: {
            ...newTopicData,
            workspace_id: memoizedWorkspaceId,
            basket_id: effectiveBasketId
          }
        };

      const result = await dispatch(addTopic(topicPayload)).unwrap();

      if (result) {
        if (currentBasket) {
          dispatch(setCurrentBasket({
            ...currentBasket,
            topics: [...currentBasket.topics, result],
            updated_at: new Date().toISOString()
          }));
        }
        setHasChanges(true);
        dispatch(setBasketContentChanged(true));

        navigate(`/workspace/${memoizedWorkspaceId}`, {
          state: { 
            workspaceName: memoizedWorkspaceName,
            activeTab: 'Baskets',
            hasChanges: true,
            updatedBasketId: effectiveBasketId,
            searchTerm: searchTerm
          }
        });
      }
    } catch (error) {
      console.error('Error creating new topic:', error);
      toast.error('Failed to create new topic');
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId, currentBasket, navigate, memoizedWorkspaceName, searchTerm]);

  // Handle adding existing topics
  const handleAddExistingTopics = useCallback(async (selectedTopics) => {
    const updatedTopics = [];
    
    for (const topic of selectedTopics) {
      try {
        const result = await dispatch(changeTopic({ 
          topicId: topic.id, 
          newBasketId: effectiveBasketId, 
          workspaceId: memoizedWorkspaceId 
        })).unwrap();
        
        if (result) updatedTopics.push(result);
      } catch (error) {
        console.error('Error moving topic:', error);
      }
    }

    if (updatedTopics.length > 0) {
      dispatch(setCurrentBasket({
        ...currentBasket,
        topics: [...currentBasket.topics, ...updatedTopics]
      }));
      setHasChanges(true);
      dispatch(setBasketContentChanged(true));
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId, currentBasket]);

  // Handler for navigating back to the workspace view
  const handleBackToWorkspace = useCallback(() => {
    navigate(`/workspace/${memoizedWorkspaceId}`, {
      state: { 
        workspaceName: memoizedWorkspaceName,
        activeTab: 'Baskets', 
        hasChanges,
        searchTerm: searchTerm
      }
    });
  }, [navigate, memoizedWorkspaceId, memoizedWorkspaceName, hasChanges, searchTerm]);

  // Handler for deleting the basket
  const handleDeleteBasket = useCallback(() => {
    // Implement delete logic here
    console.log('Delete basket');
  }, []);

  // Handler for editing the basket
  const handleEditBasket = useCallback(async (updatedData) => {
    try {
      const result = await dispatch(updateBasket({ 
        basketId: effectiveBasketId, 
        updatedData, 
        workspaceId: memoizedWorkspaceId 
      })).unwrap();
      
      if (result) {
        dispatch(setCurrentBasket(result));
        toast.success('Basket updated successfully');
        setEditBasketModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating basket:', error);
      toast.error(`Failed to update basket: ${error.message}`);
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId]);

  const handleSaveRelation = useCallback(async (updatedTopic) => {
    try {
      const result = await dispatch(updateTopic({
        topicId: updatedTopic.id,
        updates: { relation_description: updatedTopic.relation_description },
        workspaceId: memoizedWorkspaceId ?? updatedTopic.workspace_id,
      })).unwrap();

      if (result?.topic && currentBasket?.topics) {
        const updatedTopics = currentBasket.topics.map((topic) =>
          topic.id === result.topic.id ? result.topic : topic
        );
        dispatch(setCurrentBasket({
          ...currentBasket,
          topics: updatedTopics,
        }));
      }

      setShowRelationModal(false);
      setSelectedTopic(null);
      toast.success('Relationship description updated');
      return true;
    } catch (error) {
      console.error('Failed to update relationship:', error);
      toast.error('Failed to update relationship');
      return false;
    }
  }, [dispatch, memoizedWorkspaceId, currentBasket]);

  // Modify the useEffect to add a delay before showing the celebration
  useEffect(() => {
    if (!currentBasket || !currentBasket.topics || currentBasket.topics.length === 0) {
      return;
    }
    
    const totalTopics = currentBasket.topics.length;
    const ripeTopics = currentBasket.topics.filter(topic => topic.is_ripe).length;
    const masteryPercentage = Math.round((ripeTopics / totalTopics) * 100);
    
    // Only show celebration when mastery changes from <100% to 100%
    if (previousMasteryPercentage !== null && 
        previousMasteryPercentage < 100 && 
        masteryPercentage === 100) {
      
      // Clear any existing timeout
      if (celebrationTimeout) {
        clearTimeout(celebrationTimeout);
      }
      
      // Set a 1-second delay before showing the celebration
      const timeout = setTimeout(() => {
        setShowMasteryCelebration(true);
      }, 1000); // 1 seconds delay
      
      setCelebrationTimeout(timeout);
    }
    
    setPreviousMasteryPercentage(masteryPercentage);
    
    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (celebrationTimeout) {
        clearTimeout(celebrationTimeout);
      }
    };
  }, [currentBasket, previousMasteryPercentage, celebrationTimeout]);

  // Render loading state, error message, or basket content
  if (!currentBasket) {
    return (
      <div className="text-center py-8">
        <p className="text-xl font-semibold mb-4">Loading basket data...</p>
      </div>
    );
  }

  return (
    <div className="bg-neutral-50 min-h-screen pb-24 sm:pb-20">
      <BasketHeader 
        onBack={handleBackToWorkspace} 
        title={currentBasket?.name || 'Loading...'}
        description={currentBasket?.description}
        onEdit={() => setEditBasketModalOpen(true)}
        onDelete={handleDeleteBasket}
      />
      
      <OfflineStatusBanner />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <BasketStats basket={currentBasket} />

        {/* Pro Tip for basket with items */}
        {basketTopics.length > 0 && (
          <TipBox
            pageId="basket-content"
            tipId="connect-items-tip"
            title="Connect to Items Inside (Not the Basket Name)"
            message={
              <>
                Don't focus on matching the basket name. Instead, <span className="font-semibold">connect to items already in this basket</span> - this is how your basket grows meaningfully. Add descriptions explaining the relationship: <span className="italic">"Like Cranial Nerve 10, Multiple Myeloma requires at least 10% plasma cells"</span> or <span className="italic">"Iron and Ca are given 4 hours apart from Levothyroxine like the 4 hours cutoff time for proximal leg US in DVT"</span>
              </>
            }
            type="pro"
            position="top"
            showAgainButton={true}
          />
        )}

        {basketTopics.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {basketTopics.map((topic, index) => (
              <TopicItem
                key={topic.id}
                topic={topic}
                displayNumber={index + 1}
                onRemove={handleRemoveTopicFromBasket}
                onEdit={handleEditTopic}
                onSaveRelation={handleSaveRelation}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 mb-16 sm:mb-20">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-l-[#8B4513] border border-black rounded-xl p-4 sm:p-6 mb-6 max-w-2xl mx-auto">
              <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-3">
                How to Add an Item to a Basket
              </h3>
              <p className="text-sm sm:text-base text-neutral-900 mb-4">
                Go to the <span className="font-semibold">Items tab</span>, click an item, and add it to this basket or create a new one.
              </p>
              {memoizedWorkspaceId && (
                <button
                  onClick={() => navigate(`/workspace/${memoizedWorkspaceId}`, {
                    state: {
                      activeTab: 'Items',
                      workspaceName: memoizedWorkspaceName,
                      workspaceId: memoizedWorkspaceId,
                      searchTerm: searchTerm
                    }
                  })}
                  className="mt-4 px-4 py-2 bg-[#8B4513] text-white rounded-lg hover:bg-[#6B3410] transition-colors duration-200 font-medium text-sm sm:text-base"
                >
                  Go to Items Tab
                </button>
              )}
              <p className="text-xs sm:text-sm text-neutral-700 italic mt-4">
                We're working on making this process more efficient and hope to allow you to add topics directly from a basket in the future.
              </p>
            </div>
            <p className="text-lg sm:text-xl font-semibold text-neutral-900">
              No topics available in this basket.
            </p>
          </div>
        )}

        {/* Add Item to Basket Button - Fixed at bottom */}
        {memoizedWorkspaceId && (
          <div className="fixed bottom-0 left-0 right-0 p-3 sm:p-4 bg-white border-t-2 border-neutral-200">
            <div className="max-w-sm mx-auto">
              <button
                onClick={() => navigate(`/workspace/${memoizedWorkspaceId}`, {
                  state: {
                    activeTab: 'Items',
                    workspaceName: memoizedWorkspaceName,
                    workspaceId: memoizedWorkspaceId,
                    searchTerm: searchTerm
                  }
                })}
                className="
                  bg-cranberry-700 text-white 
                  px-3 sm:px-4 py-1.5 
                  rounded-xl 
                  border-2 border-neutral-900
                  hover:bg-cranberry-600 
                  transition-colors duration-300 
                  font-medium 
                  text-sm sm:text-base 
                  flex items-center justify-center
                  w-full
                "
              >
                <FaPlus className="mr-1.5 sm:mr-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Add Item to a Basket
              </button>
            </div>
          </div>
        )}

        {topicSelectionModalOpen && currentBasket && (
          <TopicSelectionModal
            onSelect={handleAddExistingTopics}
            onAddNew={handleAddNewTopic}
            onClose={() => setTopicSelectionModalOpen(false)}
            currentBasket={currentBasket}
            currentWorkspaceId={memoizedWorkspaceId}
          />
        )}

        {editBasketModalOpen && currentBasket && (
          <EditBasketModal
            basket={currentBasket}
            onClose={() => setEditBasketModalOpen(false)}
            onSave={handleEditBasket}
          />
        )}

        {showRelationModal && (
          <RelationDescriptionModal
            isOpen={showRelationModal}
            onClose={() => {
              setShowRelationModal(false);
              setSelectedTopic(null);
            }}
            onSave={handleSaveRelation}
            topic={selectedTopic}
          />
        )}

        {showMasteryCelebration && (
          <MasteryCelebration 
            onClose={() => setShowMasteryCelebration(false)} 
            basket={currentBasket}
          />
        )}
      </div>
    </div>
  );
};

export default BasketContent;
